import { __awaiter } from "tslib";
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import cn from 'classnames';
import { useTheme } from '../../theme';
import { BaseButton } from '../../BaseComponents';
import CdnImage from '../../CdnImage';
import AddBooking, { FormType } from '../../AddBooking';
import { reachGoal, UserGoal } from '../../analytics';
import { useConfig } from '../../context';
import { getCheckInUrl } from '../../utils';
import { useCheckinInfoQuery, useFindOrderMutation } from '@websky/graphql';
import ModalConfirm from '../../ModalConfirm';
const ACCOUNT_ROUTES = {
    orders: '/account/orders'
};
const ProfileControls = ({ className, onRefetchOrders, additionalButtons }) => {
    const { ProfileControls: css } = useTheme('ProfileControls');
    const { t } = useTranslation('Account');
    const history = useHistory();
    const routerLocation = useLocation();
    const { Account: { engineURL, openCheckinPageOnProfileControlsClick }, Engine: { checkInURL }, global: { companyInfo: { iataCode }, reserveEngines, showBookingPreview } } = useConfig();
    const [order, setOrder] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [addBookingModalType, setAddBookingModalType] = useState(null);
    const lastAddBookingModalType = useRef(null);
    // Prevent title flicker when closing the modal: addBookingModalType is set to null
    // immediately, but AddBooking still renders for a frame during the close animation.
    // Without the ref fallback, null !== FormType.Booking would briefly show the wrong title.
    const lastModalTypeRef = useRef(null);
    useEffect(() => {
        if (addBookingModalType) {
            lastModalTypeRef.current = addBookingModalType;
        }
    }, [addBookingModalType]);
    const onSetAddBookingModalType = useCallback((type) => () => {
        if (type === FormType.Checkin && openCheckinPageOnProfileControlsClick) {
            window.open(checkInURL);
            return;
        }
        lastAddBookingModalType.current = type;
        setAddBookingModalType(type);
    }, [setAddBookingModalType, openCheckinPageOnProfileControlsClick, checkInURL]);
    const onClearAddBookingModalType = useCallback(() => {
        if (addBookingModalType === FormType.Booking) {
            setOrder(null);
        }
        setAddBookingModalType(null);
    }, [addBookingModalType, setAddBookingModalType]);
    const [unlockOrder] = useFindOrderMutation();
    const { refetch } = useCheckinInfoQuery({ skip: true });
    const onBookingSubmit = useCallback(({ lastName, ticket, saveInProfile = false }) => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoading(true);
        const needToSaveInProfile = !showBookingPreview || saveInProfile;
        try {
            const { data, errors } = yield unlockOrder({
                variables: {
                    params: {
                        id: ticket,
                        secret: lastName,
                        saveInProfile: needToSaveInProfile
                    }
                }
            });
            const result = !errors && data && data.FindOrder;
            if (result) {
                onRefetchOrders === null || onRefetchOrders === void 0 ? void 0 : onRefetchOrders();
                reachGoal(UserGoal.BindOrder);
                setIsLoading(false);
                if (!needToSaveInProfile) {
                    setOrder(result);
                }
                else {
                    onClearAddBookingModalType();
                    goToOrders();
                }
                return true;
            }
            else {
                setIsLoading(false);
                setOrder(null);
                return { ticket: t('RetrieveBooking:Order not found') };
            }
        }
        catch (e) {
            return {
                ticket: t('An error occurred while executing the request. Please try again later or contact a customer support service.')
            };
        }
        finally {
            setIsLoading(false);
        }
    }), []);
    const onCheckinSubmit = useCallback(({ lastName, ticket }) => __awaiter(void 0, void 0, void 0, function* () {
        if ((reserveEngines === null || reserveEngines === void 0 ? void 0 : reserveEngines.redirectToWebsky1) && (reserveEngines === null || reserveEngines === void 0 ? void 0 : reserveEngines.checkin)) {
            window.open(getCheckInUrl(checkInURL, '', ticket, lastName, reserveEngines));
            return true;
        }
        else {
            setIsLoading(true);
            try {
                const { data, errors } = yield refetch({
                    params: {
                        id: ticket,
                        secret: lastName
                    }
                });
                const result = !errors && data && data.CheckinInfo;
                if (result.isAvailable) {
                    onClearAddBookingModalType();
                    window.open(getCheckInUrl(checkInURL, '', ticket, lastName));
                    return true;
                }
                else {
                    return { ticket: t('RetrieveBooking:Order not found') };
                }
            }
            catch (e) {
                return {
                    ticket: t('An error occurred while executing the request. Please try again later or contact a customer support service.')
                };
            }
            finally {
                setIsLoading(false);
            }
        }
    }), []);
    const goToOrders = () => {
        history.push(ACCOUNT_ROUTES.orders);
    };
    const onViewOrderClick = () => {
        onRefetchOrders === null || onRefetchOrders === void 0 ? void 0 : onRefetchOrders();
        onClearAddBookingModalType();
        goToOrders();
    };
    const openSearchFlights = () => window.open(engineURL);
    const onClearOrder = () => {
        setOrder(null);
    };
    const isOrdersPage = routerLocation.pathname === ACCOUNT_ROUTES.orders;
    return (React.createElement("div", { className: cn(className, css.wrapper) },
        React.createElement(CdnImage, { path: `https://cdn.websky.aero/content/frontend/images/aircraftImages/${iataCode.toUpperCase()}/default.png`, fallbackUrl: "", className: css.img, isBackground: true }),
        React.createElement("div", { className: css.actions },
            React.createElement(BaseButton, { variant: "smallNeitral", onClick: openSearchFlights }, t('Search flights')),
            React.createElement(BaseButton, { variant: "smallNeitral", onClick: onSetAddBookingModalType(FormType.Booking) }, t('I have a booking')),
            React.createElement(BaseButton, { variant: "smallNeitral", onClick: onSetAddBookingModalType(FormType.Checkin) }, t('Check-in')),
            !isOrdersPage && (React.createElement(BaseButton, { variant: 'smallNeitral', onClick: goToOrders }, t('My orders'))),
            additionalButtons),
        React.createElement(ModalConfirm, { size: "medium", isOpen: !!addBookingModalType, onClose: onClearAddBookingModalType, noPadding: true },
            React.createElement(AddBooking, { order: order, isLoading: isLoading, type: addBookingModalType || lastModalTypeRef.current, onClose: onClearAddBookingModalType, onSubmit: addBookingModalType === FormType.Booking ? onBookingSubmit : onCheckinSubmit, onClearOrder: onClearOrder, onViewOrderClick: onViewOrderClick }))));
};
export default ProfileControls;
